import React, { useEffect, useRef, useState } from "react";
import CryptoJS from "crypto-js";

const LazyImage = ({
  src,
  placeholderSrc,
  classProp,
  app,
  alt,
  zoomModeEnable,
}) => {
  const image_key = localStorage.getItem("image_key");
  const [imageSrc, setImageSrc] = useState(placeholderSrc);

  const [viewFullScreen, setFullScreen] = useState(false);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [showControls, setShowControls] = useState(false);

  const dragStart = useRef({ x: 0, y: 0 });
  const containerRef = useRef(null);

  async function fetchAndConvertImageToDataUri(imageUrl) {
    try {
      const decryptUrl = await decryptImageUrl(imageUrl, image_key);
      setImageSrc(decryptUrl);
    } catch (error) {
      console.log(error);
    }
  }

  function decryptImageUrl(encryptedUrl, key) {
    key = CryptoJS.enc.Utf8.parse(key.slice(0, 32));
    const encodedData = CryptoJS.enc.Base64.parse(encryptedUrl);
    const iv = CryptoJS.lib.WordArray.create(encodedData.words.slice(0, 4));
    const data = CryptoJS.lib.WordArray.create(encodedData.words.slice(4));
    const decryptedData = CryptoJS.AES.decrypt({ ciphertext: data }, key, {
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });
    return decryptedData.toString(CryptoJS.enc.Utf8);
  }

  useEffect(() => {
    fetchAndConvertImageToDataUri(src);
  }, [src]);

  const handleMouseDown = (e) => {
    if (!zoomModeEnable || scale === 1) return;
    e.preventDefault();
    setIsDragging(true);
    dragStart.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
  };

  const handleMouseMove = (e) => {
    if (!zoomModeEnable || !isDragging || scale === 1) return;
    setPosition({
      x: e.clientX - dragStart.current.x,
      y: e.clientY - dragStart.current.y,
    });
  };

  const handleMouseUp = () => {
    if (zoomModeEnable) setIsDragging(false);
  };

  const resetZoom = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  const zoomIn = () => setScale((prev) => Math.min(prev * 1.1, 5));
  const zoom3x = () => setScale(3);
  const zoomOut = () => setScale((prev) => Math.max(prev / 1.1, 1));

  if (zoomModeEnable) {
    const containerStyles = {
      overflow: "hidden",
      position: "relative",
      cursor: isDragging ? "grabbing" : scale > 1 ? "grab" : "default",
      width: app === "tiger" ? "100%" : app === "conflict" ? "30%" : "100%",
      height:
        app === "tiger" && alt === "Popup Tiger"
          ? "60vh"
          : app === "conflict"
          ? "80%"
          : "auto",
      marginLeft: app === "tiger_view" ? "5px" : undefined,
      marginTop: app === "tiger_view" ? "5px" : undefined,
      border: scale > 1 ? "2px solid rgba(0, 0, 0, 0.6)" : "none",
      borderRadius: scale > 1 ? "6px" : "0",
    };

    const imageStyles = {
      transform: `scale(${scale}) translate(${position.x / scale}px, ${
        position.y / scale
      }px)`,
      transformOrigin: "center center",
      transition: isDragging ? "none" : "transform 0.2s ease",
      userSelect: "none",
      pointerEvents: "all",
      width: "100%",
      height: "100%",
      objectFit: "contain",
    };

    const controlButtonStyle = {
      backgroundColor: "rgba(0, 0, 0, 0.6)",
      color: "#fff",
      border: "none",
      borderRadius: "4px",
      padding: "6px 10px",
      fontSize: "14px",
      cursor: "pointer",
    };

    return (
      <div
        ref={containerRef}
        style={containerStyles}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => {
          handleMouseUp();
          setShowControls(false);
        }}
        onMouseEnter={() => setShowControls(true)}
      >
        <img
          src={imageSrc}
          className={classProp}
          alt={alt}
          draggable={false}
          style={imageStyles}
        />
        {showControls && zoomModeEnable && (
          <div
            style={{
              position: "absolute",
              bottom: "10px",
              right: "10px",
              display: "flex",
              gap: "8px",
              zIndex: 100,
            }}
          >
            {scale > 1 && (
              <button onClick={resetZoom} style={controlButtonStyle}>
                Reset
              </button>
            )}
              <button onClick={zoom3x} style={controlButtonStyle}>
                3X
              </button>
            
            <button onClick={zoomIn} style={controlButtonStyle}>
              +
            </button>
            <button onClick={zoomOut} style={controlButtonStyle}>
              -
            </button>
          </div>
        )}
      </div>
    );
  }

  // === Zoom Mode Disabled ===
  return (
    <>
      {viewFullScreen ? (
        <img
          src={imageSrc}
          onClick={() => setFullScreen(false)}
          className="ImgFullScreen"
          alt="Loading..."
        />
      ) : (
        <img
          src={imageSrc}
          className={classProp}
          style={
            app === "tiger"
              ? {
                  width: "100%",
                  height: alt === "Popup Tiger" ? "60vh" : "auto",
                }
              : app === "tiger_view"
              ? {
                  marginLeft: "5px",
                  marginTop: "5px",
                  width: "100%",
                  height: "auto",
                }
              : app === "conflict"
              ? { width: "30%", height: "80%" }
              : {}
          }
          onClick={() => setFullScreen(true)}
          alt="Loading..."
        />
      )}
    </>
  );
};

export default LazyImage;
